import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, unref as _unref, resolveComponent as _resolveComponent, createVNode as _createVNode, withCtx as _withCtx, createBlock as _createBlock, toDisplayString as _toDisplayString, withModifiers as _withModifiers } from "vue"

const _hoisted_1 = { class: "flex" }
const _hoisted_2 = { class: "hidden md:flex brand-panel min-h-screen" }
const _hoisted_3 = { key: 0 }
const _hoisted_4 = { key: 1 }
const _hoisted_5 = { class: "min-h-screen flex align-items-center justify-content-center" }
const _hoisted_6 = { class: "flex justify-content-center align-items-center w-full" }
const _hoisted_7 = {
  key: 0,
  src: "img/patient-portal-long-color.png",
  alt: "Net Health Patient Portal",
  class: "pb-6 max-w-full"
}
const _hoisted_8 = {
  key: 1,
  src: "img/logo_wide_color.png",
  alt: "Net Health Patient Messaging",
  class: "pb-6 max-w-full"
}
const _hoisted_9 = { class: "flex justify-content-end gap-2" }
const _hoisted_10 = { class: "copyright-bar font-weight-light" }
const _hoisted_11 = {
  key: 0,
  class: "flex justify-content-center mb-2"
}

import {useRootStore} from '@/store';
import {Alert, CurrentUserViewModel, Modal} from '@/store/classes';
import {ExternalFacilityType} from '@/store/enums';
import axios from 'axios';
import {computed, onMounted, Ref, ref} from 'vue';
import {useI18n} from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import FormField from "@/components/common/FormField.vue3.vue"
import { z } from 'zod';
import { useFormValidation } from '@/components/common/FormValidation';
import { RequiredStringSchema } from '@/components/common/schemas';


export default /*@__PURE__*/_defineComponent({
  __name: 'Login.vue3',
  props: {
    portalLogin: { type: Boolean, default: false }
  },
  setup(__props: any) {

const props = __props;

const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const rootStore = useRootStore();

const username = ref('');
const password = ref('');
const facilityType: Ref<ExternalFacilityType> = ref(ExternalFacilityType.NhMessaging);

const setAccessToken = (token: string) => rootStore.dispatch('setAccessToken', token);
const setStaffId = (id?: number) => rootStore.dispatch('setStaffId', id);
const setPatientId = (id?: number) => rootStore.dispatch('setPatientId', id);
const setFacilityId = (id: number) => rootStore.dispatch('setFacilityId', id);

const showAlert = (alert: Alert) => rootStore.dispatch('showAlert', alert);
const showLoader = (loaderName: string) => rootStore.dispatch('showLoader', loaderName);
const hideLoader = (loaderName: string) => rootStore.dispatch('hideLoader', loaderName);
const showModal = async (modal: Modal) => rootStore.dispatch('showModal', modal);
const initialize = async () => rootStore.dispatch('initialize');
const initializePatientPortal = async () => rootStore.dispatch('initializePatientPortal');

const facilityTypes = [
    { text: t('enums.ExternalFacilityType.TherapyC'), value: ExternalFacilityType.TherapyC }, 
    { text: t('enums.ExternalFacilityType.XfitTherapy'), value: ExternalFacilityType.XfitTherapy },
    { text: t('enums.ExternalFacilityType.XfitWoundCare'), value: ExternalFacilityType.XfitWoundCare },
    { text: t('enums.ExternalFacilityType.NhMessaging'), value: ExternalFacilityType.NhMessaging }
];

const usernameRef: Ref<HTMLInputElement | null> = ref(null)

const Schema = z.object({ username: RequiredStringSchema, password: RequiredStringSchema})
const passwordModel = computed(() => ({ username: username.value, password: password.value }))
const validationState = useFormValidation<z.infer<typeof Schema>>(Schema, passwordModel, true);
const isStandaloneLogin = computed(() => 
    facilityType.value === ExternalFacilityType.NhPortal 
    || facilityType.value === ExternalFacilityType.NhMessaging 
    || facilityType.value === ExternalFacilityType.TherapyC
)

const resetPasswordUrl = computed(() =>
    facilityType.value === ExternalFacilityType.XfitTherapy 
        ? `${process.env.VUE_APP_XFIT_THERAPY_BASE_URL}/Security/ResetPassword`
        : facilityType.value === ExternalFacilityType.XfitWoundCare
            ? `${process.env.VUE_APP_XFIT_WOUNDCARE_BASE_URL}/Security/ResetPassword`
            : ''
)

onMounted(async () => {
    facilityType.value = props.portalLogin ? ExternalFacilityType.NhPortal
        : (route.query?.facilityType as ExternalFacilityType || ExternalFacilityType.NhMessaging);
    usernameRef.value?.focus(); 
    if (route.query.invalidToken) {
        await showAlert({ text: 'Unable to log in, please try again', type: 'error' });
    }
});


async function login(): Promise<void> {
    if (!validationState.validate()) {
        return;
    }
            
    try {
        await showLoader('login');
        
        const authResponse = await axios({
            url: isStandaloneLogin.value ? '/auth/messaging' : '/auth/sso',
            method: 'post',
            data: {
                username: username.value,
                password: password.value,
                applicationName: facilityTypeToAppName(facilityType.value)
            }
        });
                
        if (!authResponse.data) {
            await showAlert({ text: 'Unable to log in, please try again', type: 'error' });
            return;
        }
                
        await setAccessToken(authResponse.data);
                
        const user = (await axios.get<CurrentUserViewModel>('/auth/user/current', { params: getFacilityAuthParams() })).data;
        const facilityId = await getUserFacilityId(user);
        if (!facilityId) {
            await showAlert({ text: 'No facility selected, please try again', type: 'error' });
            return;
        }

        if (props.portalLogin) {
            await Promise.all([
                setPatientId(user.contact.id),
                setFacilityId(facilityId),
            ]);
            await initializePatientPortal();
            await router.push('/home');
        } else {
            await Promise.all([
                setStaffId(user.contact.id),
                setFacilityId(facilityId),
            ]);
            await initialize();
            await router.push('/alerts/messages');
        }
    } catch (e: any) {
        console.error(e);
        await showAlert({ 
            text: ((e?.response?.status === 401 && e.response.data)
                ? e.response.data
                : 'An error has occurred, please try again'), 
            type: 'error' 
        });
    } finally {
        await hideLoader('login');
    }
}

function getFacilityAuthParams(): { facilityIdEnc?: string; facilityId?: number} {
    const facilityIdParam = (route.query.facilityId || route.query.facility) as string;
    if (!isNaN(facilityIdParam as any)) {
        return { facilityId: parseInt(facilityIdParam) }
    } else {
        return { facilityIdEnc: facilityIdParam }
    }
}

async function getUserFacilityId(user: CurrentUserViewModel): Promise<number | undefined> {
    let facilityId;

    if (user.facilities.length === 1 || props.portalLogin) {
        facilityId = user.facilities[0].id;
    } else if (user.facilities.length > 1) {
        await hideLoader('login');
        facilityId = await showModal({ name: 'FacilitySelectModal', props: { facilities: user.facilities } });
        await showLoader('login');
    }

    return facilityId;
}

function facilityTypeToAppName(facilityType: ExternalFacilityType): string | null {
    switch (facilityType) {
    case ExternalFacilityType.XfitTherapy:
        return 'ReDoc';
    case ExternalFacilityType.XfitWoundCare:
        return 'WoundExpert';
    case ExternalFacilityType.NhMessaging:
        return 'NhMessaging';
    case ExternalFacilityType.TherapyC:
        return 'NhMessaging';
    case ExternalFacilityType.NhPortal:
        return 'NhPortal';
    default:
        return null;
    }
}
const openResetPassword = () => window.open(resetPasswordUrl.value, '_blank');

return (_ctx: any,_cache: any) => {
  const _component_p_input_text = _resolveComponent("p-input-text")!
  const _component_p_dropdown = _resolveComponent("p-dropdown")!
  const _component_p_button = _resolveComponent("p-button")!
  const _component_router_link = _resolveComponent("router-link")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      (_ctx.portalLogin)
        ? (_openBlock(), _createElementBlock("span", _hoisted_3, [...(_cache[4] || (_cache[4] = [
            _createTextVNode(" Your Access To ", -1),
            _createElementVNode("br", null, null, -1),
            _createTextVNode(" Better Care. ", -1)
          ]))]))
        : (_openBlock(), _createElementBlock("span", _hoisted_4, [...(_cache[5] || (_cache[5] = [
            _createTextVNode(" Reuniting Caregivers With ", -1),
            _createElementVNode("br", null, null, -1),
            _createTextVNode(" Their Calling. ", -1)
          ]))]))
    ]),
    _createElementVNode("div", _hoisted_5, [
      _createElementVNode("div", _hoisted_6, [
        _createVNode(FormField, { class: "p-0 sm:col-9 lg:col-7 xl:col-5" }, {
          default: _withCtx(() => [
            (_ctx.portalLogin)
              ? (_openBlock(), _createElementBlock("img", _hoisted_7))
              : (_openBlock(), _createElementBlock("img", _hoisted_8)),
            _createVNode(FormField, {
              "validation-state": _unref(validationState).result.value.username,
              "label-key": "labels.user.username"
            }, {
              default: _withCtx(({ onBlur }) => [
                _createVNode(_component_p_input_text, {
                  modelValue: username.value,
                  "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((username).value = $event)),
                  autofocus: "",
                  class: "w-full",
                  onBlur: onBlur
                }, null, 8, ["modelValue", "onBlur"])
              ]),
              _: 1
            }, 8, ["validation-state"]),
            _createVNode(FormField, {
              "validation-state": _unref(validationState).result.value.password,
              "label-key": "labels.user.password"
            }, {
              default: _withCtx(({ onBlur }) => [
                _createVNode(_component_p_input_text, {
                  modelValue: password.value,
                  "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((password).value = $event)),
                  class: "w-full",
                  type: "password",
                  onBlur: onBlur
                }, null, 8, ["modelValue", "onBlur"])
              ]),
              _: 1
            }, 8, ["validation-state"]),
            (!props.portalLogin)
              ? (_openBlock(), _createBlock(FormField, {
                  key: 2,
                  "label-key": "labels.user.application"
                }, {
                  default: _withCtx(() => [
                    _createVNode(_component_p_dropdown, {
                      modelValue: facilityType.value,
                      "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((facilityType).value = $event)),
                      options: facilityTypes,
                      class: "w-full",
                      "option-label": "text",
                      "option-value": "value"
                    }, null, 8, ["modelValue"])
                  ]),
                  _: 1
                }))
              : _createCommentVNode("", true),
            _createElementVNode("div", _hoisted_9, [
              (isStandaloneLogin.value)
                ? (_openBlock(), _createBlock(_component_p_button, {
                    key: 0,
                    outlined: "",
                    onClick: _cache[3] || (_cache[3] = ($event: any) => (showModal({ name: 'ResetLoginModal' })))
                  }, {
                    default: _withCtx(() => [
                      _createTextVNode(_toDisplayString(_unref(t)('actions.resetPassword')), 1)
                    ]),
                    _: 1
                  }))
                : (_openBlock(), _createBlock(_component_p_button, {
                    key: 1,
                    outlined: "",
                    onClick: openResetPassword
                  }, {
                    default: _withCtx(() => [
                      _createTextVNode(_toDisplayString(_unref(t)('actions.resetPassword')), 1)
                    ]),
                    _: 1
                  })),
              _createVNode(_component_p_button, {
                onClick: _withModifiers(login, ["prevent"])
              }, {
                default: _withCtx(() => [
                  _createTextVNode(_toDisplayString(_unref(t)('actions.login')), 1)
                ]),
                _: 1
              })
            ])
          ]),
          _: 1
        })
      ]),
      _createElementVNode("div", _hoisted_10, [
        (_ctx.portalLogin)
          ? (_openBlock(), _createElementBlock("div", _hoisted_11, [
              _createVNode(_component_router_link, { to: "/terms-and-conditions" }, {
                default: _withCtx(() => [...(_cache[6] || (_cache[6] = [
                  _createTextVNode(" Terms and Conditions of Use ", -1)
                ]))]),
                _: 1
              }),
              _cache[8] || (_cache[8] = _createElementVNode("div", { class: "mx-2" }, " | ", -1)),
              _createVNode(_component_router_link, { to: "/privacy-policy" }, {
                default: _withCtx(() => [...(_cache[7] || (_cache[7] = [
                  _createTextVNode(" Privacy Policy ", -1)
                ]))]),
                _: 1
              })
            ]))
          : _createCommentVNode("", true),
        _createElementVNode("div", null, " Copyright © " + _toDisplayString(new Date().getFullYear()) + " - Net Health. All Rights Reserved. ", 1)
      ])
    ])
  ]))
}
}

})